<template>
    <div>
        <div class="demo-spin-article">
            <!-- Loan picker: Step 1 filter+search, Step 2 pick -->
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">
                        {{ $t("moveLoanWriteOff.loan") }}
                    </label>
                    <div class="tw-flex tw-items-center tw-space-x-2 tw-mb-1">
                        <Select
                            v-model="filterBranchId"
                            filterable
                            clearable
                            placeholder="Filter by branch (optional)"
                            style="width: 240px"
                            @on-change="onFilterBranchChange"
                        >
                            <Option
                                v-for="b in branches"
                                :value="b.branch_id"
                                :key="b.branch_id"
                                :label="
                                    b.branch_code + ' | ' + b.branch_name_kh
                                "
                            >
                                {{ b.branch_code }} | {{ b.branch_name_kh }}
                            </Option>
                        </Select>
                        <Input
                            search
                            enter-button="Search"
                            v-model="loanSearch"
                            placeholder="Search: loan # | customer code | customer name | contact no"
                            :disabled="loanLoading"
                            @on-search="onSearchLoan"
                            class="tw-flex-1"
                        />
                    </div>
                    <div
                        class="tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-gray-500 tw-mb-2"
                    >
                        <span v-if="loanLoading" class="tw-text-blue-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i>
                            Searching...
                        </span>
                        <span v-else>
                            <i class="fa fa-info-circle tw-mr-1"></i>
                            Type keyword then press Enter or click Search
                        </span>
                        <span
                            v-if="hasSearched && !loanLoading"
                            class="tw-font-semibold"
                        >
                            {{ loanWriteOffs.length }} result(s)
                        </span>
                    </div>
                    <Select
                        v-model="model.loan_id"
                        filterable
                        clearable
                        :disabled="loanWriteOffs.length === 0"
                        :placeholder="
                            loanWriteOffs.length === 0
                                ? 'Search first to load written-off loan list'
                                : 'Select a written-off loan from the results'
                        "
                        :class="{
                            'ivu-form-item-error': errors.has('loan_id')
                        }"
                        @on-change="onLoanChange"
                    >
                        <Option
                            v-for="(item, index) in loanWriteOffs"
                            :value="item.loan_id"
                            :key="index"
                            :label="item.loan_number"
                        >
                            {{ item.loan_number }} | {{ item.customer_code }} |
                            {{ item.customer_name_en }} |
                            {{ item.customer_name_kh }} |
                            {{ item.contact_no }}
                        </Option>
                    </Select>
                    <div
                        class="text-danger tw-text-xs tw-mt-1"
                        v-if="errors.has('loan_id')"
                    >
                        {{ errors.first("loan_id") }}
                    </div>
                </div>
            </div>

            <!-- Current branch + CO snapshot -->
            <div v-if="selectedLoan" class="list-group-item mb-3">
                <div class="tw-flex tw-justify-between tw-text-sm">
                    <div>
                        <span class="text-gray-600">Current Branch:</span>
                        <strong class="tw-ml-1">{{
                            selectedLoan.current_branch_code || "—"
                        }}</strong>
                    </div>
                    <div>
                        <span class="text-gray-600">Current CO:</span>
                        <strong class="tw-ml-1">{{
                            selectedLoan.current_co_name_en || "—"
                        }}</strong>
                    </div>
                </div>
            </div>

            <!-- New Branch (all branches, including the current one) -->
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">
                        {{ $t("moveLoanWriteOff.newBranch") }}
                    </label>
                    <Select
                        v-model="model.new_branch_id"
                        filterable
                        clearable
                        placeholder="Select destination branch"
                        :class="{
                            'ivu-form-item-error': errors.has('new_branch_id')
                        }"
                        @on-change="onBranchChange"
                    >
                        <Option
                            v-for="b in branches"
                            :value="b.branch_id"
                            :key="b.branch_id"
                            :label="
                                b.branch_code + ' | ' + (b.branch_name_kh || '')
                            "
                        >
                            {{ b.branch_code }} | {{ b.branch_name_kh || "" }}
                        </Option>
                    </Select>
                    <div
                        class="text-danger tw-text-xs tw-mt-1"
                        v-if="errors.has('new_branch_id')"
                    >
                        {{ errors.first("new_branch_id") }}
                    </div>
                </div>
            </div>

            <!-- New CO -->
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">
                        {{ $t("moveLoanWriteOff.newCo") }}
                        <span
                            v-if="coLoading"
                            class="tw-ml-2 tw-text-xs tw-text-blue-500"
                        >
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i>
                            Loading credit officers...
                        </span>
                    </label>
                    <Select
                        v-model="model.new_co_id"
                        filterable
                        clearable
                        :loading="coLoading"
                        :disabled="
                            coLoading ||
                                !model.new_branch_id ||
                                employeesByBranch.length === 0
                        "
                        :placeholder="
                            coLoading
                                ? 'Loading credit officers...'
                                : !model.new_branch_id
                                ? 'Pick a new branch first'
                                : employeesByBranch.length === 0
                                ? 'No active employees in this branch'
                                : 'Select destination CO'
                        "
                        :class="{
                            'ivu-form-item-error': errors.has('new_co_id')
                        }"
                    >
                        <Option
                            v-for="emp in employeesByBranch"
                            :value="emp.employee_id"
                            :key="emp.employee_id"
                            :label="
                                (emp.card_id || '') +
                                    ' | ' +
                                    emp.employee_name_en
                            "
                        >
                            {{ emp.card_id }} | {{ emp.employee_name_en }}
                        </Option>
                    </Select>
                    <div
                        class="text-danger tw-text-xs tw-mt-1"
                        v-if="errors.has('new_co_id')"
                    >
                        {{ errors.first("new_co_id") }}
                    </div>
                </div>
            </div>

            <!-- Reason (optional) -->
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label">
                        {{ $t("moveLoanWriteOff.reason") }}
                    </label>
                    <textarea
                        v-model="model.reason"
                        class="form-control"
                        rows="2"
                        placeholder="Optional"
                    ></textarea>
                </div>
            </div>

            <div slot="footer">
                <div class="tw-flex tw-justify-end tw-space-x-2">
                    <ts-button
                        @click.prevent="() => $emit('cancel')"
                        class="btn-gray"
                    >
                        {{ $t("cancel") }}
                    </ts-button>
                    <ts-button
                        color="primary"
                        :waiting="waiting"
                        @click.prevent="onSave"
                    >
                        {{ $t("save") }}
                    </ts-button>
                </div>
            </div>
        </div>
        <Spin size="large" fix v-if="loading"></Spin>
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import { mapActions, mapState } from "vuex";

export default {
    name: "moveLoanWriteOffForm",
    data() {
        return {
            errors: new Errors(),
            waiting: false,
            loading: false,
            loanLoading: false,
            coLoading: false,
            loanSearch: "",
            filterBranchId: null,
            hasSearched: false,
            model: {
                loan_id: null,
                new_branch_id: null,
                new_co_id: null,
                reason: null
            }
        };
    },
    computed: {
        ...mapState("creditOperation/moveLoanWriteOff", [
            "loanWriteOffs",
            "branches",
            "employeesByBranch"
        ]),
        selectedLoan() {
            if (!this.model.loan_id) return null;
            return (
                (this.loanWriteOffs || []).find(
                    l => l.loan_id === this.model.loan_id
                ) || null
            );
        }
    },
    methods: {
        ...mapActions("creditOperation/moveLoanWriteOff", [
            "getLoanWriteOffs",
            "getBranches",
            "getEmployeesByBranch"
        ]),
        async openForm() {
            this.clearInput();
            // Preload branches once per dialog open.
            this.loading = true;
            try {
                await this.getBranches();
            } finally {
                this.loading = false;
            }
        },
        onSearchLoan() {
            // Allow searching with branch-filter only, or with keyword, or both.
            if (!this.loanSearch && !this.filterBranchId) return;
            this.errors = new Errors();
            this.loanLoading = true;
            this.getLoanWriteOffs({
                search: this.loanSearch || undefined,
                branch_id: this.filterBranchId || undefined
            })
                .then(() => {
                    this.hasSearched = true;
                })
                .finally(() => {
                    this.loanLoading = false;
                });
        },
        onFilterBranchChange() {
            // Clear previously loaded loans so stale results don't leak across filters.
            this.$store.commit(
                "creditOperation/moveLoanWriteOff/SET_LOAN_WRITE_OFFS",
                []
            );
            this.model.loan_id = null;
            this.hasSearched = false;
        },
        onLoanChange() {
            // Reset downstream pickers if the user re-selects a different loan.
            if (!this.model.loan_id) return;
        },
        onBranchChange(branchId) {
            this.model.new_co_id = null;
            if (!branchId) {
                this.$store.commit(
                    "creditOperation/moveLoanWriteOff/SET_EMPLOYEES_BY_BRANCH",
                    []
                );
                return;
            }
            this.coLoading = true;
            this.getEmployeesByBranch({ branch_id: branchId }).finally(() => {
                this.coLoading = false;
            });
        },
        onSave() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("creditOperation/moveLoanWriteOff/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id === 0 ? "success" : "warning",
                        text: response.message
                    });
                    if (response.message_id === 0) {
                        this.$emit("cancel");
                        this.$emit("fetchData");
                    }
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        clearInput() {
            this.loanSearch = "";
            this.filterBranchId = null;
            this.hasSearched = false;
            this.model.loan_id = null;
            this.model.new_branch_id = null;
            this.model.new_co_id = null;
            this.model.reason = null;
            this.$store.commit(
                "creditOperation/moveLoanWriteOff/CLEAR_RESOURCES"
            );
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "MOVE LOAN WRITE-OFF",
                desc: not.text
            });
        }
    }
};
</script>
